import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  Search,
  Wrench,
  ArrowRight,
  XCircle,
  CheckCircle2,
  FileText,
  ListPlus
} from "lucide-react";
import { years } from "@/lib/formData";
import { toast } from "@/components/ui/use-toast";
import { TypeAnimation } from "react-type-animation";
import { useDispatch, useSelector } from "react-redux";
import {
  searchParts,
  fetchCategoriesAndMakes,
  fetchModelsByMake
} from "../../store/parts/partsAction";

const HeroSection = ({ variants }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    isSearching,
    makes: dynamicMakes,
    partModel: dynamicModels
  } = useSelector((state) => state.parts);

  const [searchYear, setSearchYear] = useState("");
  const [searchMake, setSearchMake] = useState("");
  const [searchModel, setSearchModel] = useState("");
  const [searchPart, setSearchPart] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [customMake, setCustomMake] = useState("");
  const [customModel, setCustomModel] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [queryDisplay, setQueryDisplay] = useState("");

  const ANY_YEAR_VALUE = "all-years";
  const ANY_MAKE_VALUE = "all-makes";
  const ANY_MODEL_VALUE = "all-models";
  const OTHER_MAKE_VALUE = "other-make";
  const OTHER_MODEL_VALUE = "other-model";

  useEffect(() => {
    dispatch(fetchCategoriesAndMakes());
  }, [dispatch]);

  useEffect(() => {
    if (
      searchMake &&
      searchMake !== ANY_MAKE_VALUE &&
      searchMake !== OTHER_MAKE_VALUE
    ) {
      const selectedMake = dynamicMakes?.find(
        (make) => make.name === searchMake
      );
      if (selectedMake?.id) {
        dispatch(fetchModelsByMake(selectedMake.id));
      }
    } else {
      setAvailableModels([]);
    }
  }, [searchMake, dynamicMakes, dispatch]);

  useEffect(() => {
    if (dynamicModels && searchMake !== OTHER_MAKE_VALUE) {
      setAvailableModels(dynamicModels.map((model) => model.name));
    }
  }, [dynamicModels, searchMake]);

  // Helper function to sanitize filter values
  const sanitizeFilterValue = (value) => {
    if (!value) return value;
    // Replace spaces and special characters with underscores or remove them
    return value.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");
  };

  const handleSearchPartChange = useCallback((e) => {
    setSearchPart(e.target.value);
  }, []);

  const handleYearChange = useCallback((value) => {
    setSearchYear(value === ANY_YEAR_VALUE ? "" : value);
    setSearchResults(null); // Reset results on filter change
  }, []);

  const handleMakeChange = useCallback((value) => {
    const actualValue = value === ANY_MAKE_VALUE ? "" : value;
    setSearchMake(actualValue);
    setSearchModel("");
    setCustomMake("");
    setSearchResults(null);
  }, []);

  const handleModelChange = useCallback((value) => {
    const actualValue = value === ANY_MODEL_VALUE ? "" : value;
    setSearchModel(actualValue);
    setCustomModel("");
    setSearchResults(null);
  }, []);

  const handleCustomMakeChange = useCallback((e) => {
    setCustomMake(e.target.value);
    setSearchResults(null);
  }, []);

  const handleCustomModelChange = useCallback((e) => {
    setCustomModel(e.target.value);
    setSearchResults(null);
  }, []);

  const handleSearch = async () => {
    try {
      // Validate at least one search criteria
      if (
        !searchPart.trim() &&
        !searchYear &&
        !searchMake &&
        !searchModel &&
        !customMake
      ) {
        toast({
          title: "Search Error",
          description: "Please enter at least one search criteria.",
          variant: "destructive"
        });
        return;
      }

      // Validate custom make/model
      if (searchMake === OTHER_MAKE_VALUE && !customMake.trim()) {
        toast({
          title: "Search Error",
          description: "Please enter a make when selecting 'Other'.",
          variant: "destructive"
        });
        return;
      }

      if (searchModel === OTHER_MODEL_VALUE && !customModel.trim()) {
        toast({
          title: "Search Error",
          description: "Please enter a model when selecting 'Other'.",
          variant: "destructive"
        });
        return;
      }

      // Prepare search parameters with sanitized values
      const finalMake =
        searchMake === OTHER_MAKE_VALUE ? customMake : searchMake;
      const finalModel =
        searchModel === OTHER_MODEL_VALUE ? customModel : searchModel;

      // Sanitize the values to prevent filter parsing errors
      const sanitizedMake = sanitizeFilterValue(finalMake);
      const sanitizedModel = sanitizeFilterValue(finalModel);

      const { results, queryDisplay: query } = await dispatch(
        searchParts({
          searchPart,
          searchYear,
          searchMake: sanitizedMake,
          searchModel: sanitizedModel
        })
      ).unwrap();

      console.log("results: ", results);

      // Store results instead of immediately navigating
      setSearchResults(results);
      setQueryDisplay(query);
    } catch (err) {
      console.error(
        "Search Error",
        err.message || "Something went wrong during search."
      );
      toast({
        variant: "destructive",
        title: "Search Error",
        description: err.message || "Something went wrong during search."
      });
      setSearchResults([]);
    }
  };

  const handleViewResults = () => {
    navigate("/search-results", {
      state: {
        query: queryDisplay,
        results: searchResults,
        hasResults: searchResults && searchResults.length > 0
      }
    });
  };

  const handleNewSearch = () => {
    setSearchResults(null);
    setSearchYear("");
    setSearchMake("");
    setSearchModel("");
    setSearchPart("");
    setCustomMake("");
    setCustomModel("");
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const handleInquiryClick = () => {
    const finalMake = searchMake === OTHER_MAKE_VALUE ? customMake : searchMake;
    const finalModel =
      searchModel === OTHER_MODEL_VALUE ? customModel : searchModel;

    const inquiryPayload = [searchPart, searchYear, finalMake, finalModel]
      .filter(Boolean)
      .join(" ");

    navigate("/inquire-part", { state: { query: inquiryPayload } });
    setSearchResults(null);
    setSearchYear("");
    setSearchMake("");
    setSearchModel("");
    setSearchPart("");
    setCustomMake("");
    setCustomModel("");
  };

  const showCustomMakeInput = searchMake === OTHER_MAKE_VALUE;
  const showCustomModelInput = searchModel === OTHER_MODEL_VALUE;
  const hasSearched = searchResults !== null;
  const hasResults = searchResults && searchResults.length > 0;

  return (
    <motion.section
      id="hero-section"
      className="relative bg-gradient-to-br from-secondary via-background to-secondary/70 text-foreground py-20 md:py-32 overflow-hidden"
      variants={variants}
    >
      <div className="absolute inset-0 opacity-10">
        <img
          alt="Abstract background pattern of gears and tools"
          className="w-full h-full object-cover"
          src="https://images.unsplash.com/photo-1594383538644-66fcb99ae1ed"
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
          <motion.div
            className="text-center md:text-left"
            variants={itemVariants}
          >
            <motion.div
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text min-h-[100px] md:min-h-[150px] lg:min-h-[180px]"
              variants={itemVariants}
            >
              <TypeAnimation
                sequence={["Find Quality Recycled Auto Parts, Fast!", 3000]}
                wrapper="h1"
                speed={50}
                style={{ display: "inline-block" }}
                repeat={Infinity}
              />
            </motion.div>
            <motion.p
              className="text-base md:text-lg lg:text-xl text-muted-foreground mb-8 max-w-xl mx-auto md:mx-0"
              variants={itemVariants}
            >
              Your one-stop hub for connecting with businesses offering reliable
              used and recycled auto components. Save money and support
              sustainability.
            </motion.p>
          </motion.div>

          <motion.div
            className="bg-card/80 backdrop-blur-lg p-6 sm:p-8 rounded-xl shadow-2xl border border-border"
            variants={itemVariants}
          >
            <h3 className="text-2xl font-semibold mb-6 text-center text-primary flex items-center justify-center">
              <Wrench className="mr-3 h-7 w-7" /> Search by Part
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="search-year-hero"
                    className="block text-sm font-medium text-muted-foreground mb-2"
                  >
                    Year
                  </label>
                  <Select
                    value={searchYear || ANY_YEAR_VALUE}
                    onValueChange={handleYearChange}
                    disabled={hasSearched}
                  >
                    <SelectTrigger
                      id="search-year-hero"
                      className="w-full h-11"
                    >
                      <SelectValue placeholder="Select Year" />
                    </SelectTrigger>
                    <SelectContent
                      className="max-h-60 overflow-y-auto"
                      position="popper"
                      side="bottom"
                    >
                      <SelectItem value={ANY_YEAR_VALUE}>Any Year</SelectItem>
                      {years.map((year) => (
                        <SelectItem
                          key={`hero-year-${year}`}
                          value={year.toString()}
                        >
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label
                    htmlFor="search-make-hero"
                    className="block text-sm font-medium text-muted-foreground mb-2"
                  >
                    Make
                  </label>
                  <Select
                    value={searchMake || ANY_MAKE_VALUE}
                    onValueChange={handleMakeChange}
                    disabled={hasSearched}
                  >
                    <SelectTrigger
                      id="search-make-hero"
                      className="w-full h-11"
                    >
                      <SelectValue placeholder="Select Make" />
                    </SelectTrigger>
                    <SelectContent
                      className="max-h-60 overflow-y-auto"
                      position="popper"
                      side="bottom"
                    >
                      <SelectItem value={ANY_MAKE_VALUE}>Any Make</SelectItem>
                      {dynamicMakes?.map((make) => (
                        <SelectItem
                          key={`hero-make-${make.id}`}
                          value={make.name}
                        >
                          {make.name}
                        </SelectItem>
                      ))}
                      <SelectItem value={OTHER_MAKE_VALUE}>
                        Other (Specify Below)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Custom Make Input */}
              <AnimatePresence>
                {showCustomMakeInput && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <label
                      htmlFor="custom-make-hero"
                      className="block text-sm font-medium text-muted-foreground mb-2"
                    >
                      Enter Make <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="custom-make-hero"
                      type="text"
                      placeholder="e.g., Tesla, Rivian, Polestar"
                      value={customMake}
                      onChange={handleCustomMakeChange}
                      className="h-11"
                      disabled={hasSearched}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <div>
                <label
                  htmlFor="search-model-hero"
                  className="block text-sm font-medium text-muted-foreground mb-2"
                >
                  Model
                </label>
                <Select
                  value={searchModel || ANY_MODEL_VALUE}
                  onValueChange={handleModelChange}
                  disabled={
                    hasSearched ||
                    !searchMake ||
                    (searchMake !== OTHER_MAKE_VALUE &&
                      availableModels.length === 0)
                  }
                >
                  <SelectTrigger id="search-model-hero" className="w-full h-11">
                    <SelectValue
                      placeholder={
                        !searchMake
                          ? "Select Make first"
                          : searchMake === OTHER_MAKE_VALUE
                          ? "Select Model"
                          : availableModels.length === 0
                          ? "Loading models..."
                          : "Select Model"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent
                    className="max-h-60 overflow-y-auto"
                    position="popper"
                    side="bottom"
                  >
                    <SelectItem value={ANY_MODEL_VALUE}>Any Model</SelectItem>
                    {availableModels.map((model) => (
                      <SelectItem key={`hero-model-${model}`} value={model}>
                        {model}
                      </SelectItem>
                    ))}
                    <SelectItem value={OTHER_MODEL_VALUE}>
                      Other (Specify Below)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <AnimatePresence>
                {showCustomModelInput && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <label
                      htmlFor="custom-model-hero"
                      className="block text-sm font-medium text-muted-foreground mb-2"
                    >
                      Enter Model <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="custom-model-hero"
                      type="text"
                      placeholder="e.g., Model S, R1T, Series 3"
                      value={customModel}
                      onChange={handleCustomModelChange}
                      className="h-11"
                      disabled={hasSearched}
                    />
                    {customModel && (
                      <p className="text-xs text-muted-foreground mt-1.5 ml-1">
                        Will be sanitized to: {sanitizeFilterValue(customModel)}
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div>
                <label
                  htmlFor="search-part-hero"
                  className="block text-sm font-medium text-muted-foreground mb-2"
                >
                  Part Name (Optional)
                </label>
                <Input
                  id="search-part-hero"
                  type="text"
                  placeholder="e.g., Alternator, Bumper, Headlight"
                  value={searchPart}
                  onChange={handleSearchPartChange}
                  className="h-11"
                  disabled={hasSearched}
                />
                {searchPart && (
                  <p className="text-xs text-muted-foreground mt-1.5 ml-1">
                    Searching for: {searchPart}
                  </p>
                )}
              </div>

              {!hasSearched ? (
                <Button
                  onClick={handleSearch}
                  className="w-full h-12 text-lg bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                  disabled={isSearching}
                >
                  {isSearching ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Search className="mr-2 h-5 w-5" /> Search Parts
                    </>
                  )}
                </Button>
              ) : (
                <div className="space-y-3">
                  <AnimatePresence mode="wait">
                    {hasResults ? (
                      <motion.div
                        key="results-found"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4"
                      >
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-green-900 dark:text-green-100 mb-1">
                              Search Successful!
                            </h4>
                            <p className="text-sm text-green-700 dark:text-green-300">
                              Found{" "}
                              <span className="font-bold">
                                {searchResults.length}
                              </span>{" "}
                              matching{" "}
                              {searchResults.length === 1
                                ? "result"
                                : "results"}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="no-results"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-6 flex flex-col gap-4 shadow-sm"
                      >
                        {/* Icon + Text */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:gap-4 items-center text-center sm:text-left">
                          <XCircle className="h-8 w-8 text-amber-600 dark:text-amber-400 flex-shrink-0 mb-2 sm:mb-0" />

                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-amber-900 dark:text-amber-100 text-lg mb-1">
                              No Results Found
                            </h4>
                            <p className="text-sm sm:text-base text-amber-700 dark:text-amber-300 leading-relaxed">
                              No matching parts were found. You can submit a
                              part inquiry to the seller and we’ll help you
                              locate it.
                            </p>
                          </div>
                        </div>

                        {/* Button */}
                        <div className="flex flex-col gap-3 mt-3 w-full">
                          <Button
                            onClick={handleInquiryClick}
                            size="md"
                            className="flex-1 sm:flex-none bg-gradient-to-r from-primary to-accent text-white shadow-lg hover:opacity-90 transition-all font-medium flex items-center justify-center gap-2 px-3 text-[10px] xs:text-sm xs:px-6 py-2 xs:py-3 rounded-lg"
                          >
                            <FileText className="xs:h-5 xs:w-5 w-4 h-4" />
                            Submit Part Inquiry
                          </Button>
                          {user && user?.role == "seller" && (
                            <Button
                              onClick={() => navigate("/dashboard/add-part")}
                              size="md"
                              variant="outline"
                              className="flex-1 sm:flex-none shadow-lg hover:opacity-90 transition-all font-medium flex items-center justify-center gap-2 px-3 text-[10px] xs:text-sm xs:px-6 py-2 xs:py-3 rounded-lg"
                            >
                              <ListPlus className="mr-2 h-4 w-4" />
                              Add Part to Inventory
                            </Button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="flex gap-3">
                    {hasResults && (
                      <Button
                        onClick={handleViewResults}
                        className="flex-1 h-12 text-base bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                      >
                        View Results <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    )}
                    <Button
                      onClick={handleNewSearch}
                      variant={hasResults ? "outline" : "default"}
                      className={`${
                        hasResults ? "flex-1" : "w-full"
                      } h-10 xs:h-12 text-base`}
                    >
                      New Search
                    </Button>
                  </div>
                </div>
              )}

              {!hasSearched && (
                <p className="text-xs text-muted-foreground text-center">
                  <strong>Tip:</strong> Search by part name alone, or combine
                  with vehicle details for precise results. Names with spaces
                  will be automatically formatted.
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default HeroSection;
